
import { useCallback } from "react";
import { Article, ArticleFormData, ArticleStatus } from "@/types/article-types";
import { useArticleLoader } from "./useArticleLoader";
import { useArticleCreator } from "./useArticleCreator";
import { useArticleUpdater } from "./useArticleUpdater";
import { useArticleDeleter } from "./useArticleDeleter";
import { supabase } from "@/integrations/supabase/client";

export const useArticleOperations = () => {
  const { 
    articles, 
    loading, 
    error,
    lastLoadTime,
    setArticles, 
    loadArticles,
    forceReloadArticles 
  } = useArticleLoader();
  
  const { createArticle } = useArticleCreator(loadArticles);
  const { updateArticle } = useArticleUpdater(loadArticles);
  const { deleteArticle } = useArticleDeleter(articles, setArticles);

  // Add a helper function to get article by ID
  const getArticleById = useCallback((id: string): Article | undefined => {
    return articles.find(article => article.id === id);
  }, [articles]);

  // Add a new function to fetch a single article by ID
  const fetchArticleById = useCallback(async (id: string): Promise<Article | null> => {
    try {
      console.log("Fetching single article with ID:", id);
      
      const { data, error } = await supabase
        .from('articles')
        .select(`
          *,
          author:author_id (
            id,
            first_name,
            last_name,
            bio,
            profile_image_url,
            created_at,
            updated_at
          ),
          tags:article_tags_map (
            tag:tag_id (
              id,
              name,
              slug
            )
          )
        `)
        .eq('id', id)
        .single();

      if (error) throw error;
      
      console.log("Single article data received:", data);
      
      // Transform tags structure to match our Article type
      const transformedArticle: Article = {
        ...data,
        status: data.status as ArticleStatus, // Explicit cast to ArticleStatus
        tags: (data.tags || [])
          .filter((tagItem: any) => tagItem && tagItem.tag)
          .map((tagItem: any) => tagItem.tag)
      };
      
      // Update the articles array with this article
      setArticles(prevArticles => {
        // Check if article already exists
        const articleExists = prevArticles.some(a => a.id === transformedArticle.id);
        
        if (articleExists) {
          // Replace the existing article
          return prevArticles.map(a => a.id === transformedArticle.id ? transformedArticle : a);
        } else {
          // Add the new article
          return [...prevArticles, transformedArticle];
        }
      });

      return transformedArticle;
    } catch (error: any) {
      console.error('Error fetching article by ID:', error);
      return null;
    }
  }, [setArticles]);

  return {
    articles,
    loading,
    error,
    lastLoadTime,
    loadArticles,
    forceReloadArticles,
    createArticle,
    updateArticle,
    deleteArticle,
    getArticleById,
    fetchArticleById
  };
};
